/**
 * 孤儿音色卡片组件
 * v10.2 新增：显示 CosyVoice 平台上存在但系统无记录的音色
 */

import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Trash2, Loader2, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { OrphanedVoice } from '@/modules/identity/types'

interface OrphanedVoiceCardProps {
  voice: OrphanedVoice
  onDelete: (modelId: string) => void
  isDeleting?: boolean
}

/**
 * CosyVoice 状态配置
 */
const COSYVOICE_STATUS_CONFIG = {
  OK: { label: '可用', color: 'text-emerald-600', bgColor: 'bg-emerald-50', icon: CheckCircle },
  DEPLOYING: { label: '审核中', color: 'text-amber-600', bgColor: 'bg-amber-50', icon: Clock },
  UNDEPLOYED: { label: '审核失败', color: 'text-red-600', bgColor: 'bg-red-50', icon: XCircle },
}

export function OrphanedVoiceCard({
  voice,
  onDelete,
  isDeleting = false,
}: OrphanedVoiceCardProps) {
  const statusConfig = COSYVOICE_STATUS_CONFIG[voice.status]
  const StatusIcon = statusConfig.icon

  return (
    <div className="flex items-center justify-between p-3 bg-amber-50/50 border border-amber-200 rounded-lg">
      <div className="flex-1 min-w-0 mr-3">
        <p
          className="text-xs font-mono text-slate-600 truncate"
          title={voice.model_id}
        >
          {voice.model_id}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge
          variant="secondary"
          className={cn('text-xs', statusConfig.bgColor, statusConfig.color)}
        >
          <StatusIcon className="h-3 w-3 mr-1" />
          {statusConfig.label}
        </Badge>

        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onDelete(voice.model_id)}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Trash2 className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  )
}

/**
 * 孤儿音色区域标题
 */
export function OrphanedVoiceHeader({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-2 text-amber-700">
      <AlertCircle className="h-4 w-4" />
      <span className="font-medium">未关联音色（孤儿音色）</span>
      {count > 0 && (
        <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
          {count} 个
        </Badge>
      )}
    </div>
  )
}
